import React, { useCallback, useEffect, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import GraphicEqOutlinedIcon from "@mui/icons-material/GraphicEqOutlined";
import MicOutlinedIcon from "@mui/icons-material/MicOutlined";
import MicOffOutlinedIcon from "@mui/icons-material/MicOffOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import CallEndOutlinedIcon from "@mui/icons-material/CallEndOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import VoiceBars from "./VoiceBars";
import CoachDialog from "./CoachDialog";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";

const OPENAI_CALLS_URL = "https://api.openai.com/v1/realtime/calls";

const transcriptNote = (transcript) =>
  `Transcripción automática de lo que acaba de decir el cliente: «${transcript}». Si no está segura de lo que escuchó en el audio, guíese por esta transcripción. Si ninguna de las dos es clara, pídale que lo repita.`;
const END_CALL_DELAY_MS = 4000;
const REPLY_DEBOUNCE_MS = 700;
const DICTATION_DEBOUNCE_MS = 2000;
const MIN_SPEECH_DURATION_MS = 350;
const REAL_SPEECH_PATTERN = /[\p{Script=Latin}\p{Nd}]/u;
const DICTATION_PATTERN =
  /\d|\b(cero|uno|una|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|once|doce|trece|catorce|quince|dieci[a-záéíóúñ]*|veinte|veinti[a-záéíóúñ]*|treinta|cuarenta|cincuenta|sesenta|setenta|ochenta|noventa|cien|ciento|mil|zero|one|two|three|four|five|six|seven|eight|nine|ten)\b/i;

const emptyVoiceState = () => ({
  pc: null,
  dc: null,
  stream: null,
  audio: null,
  pendingTrace: [],
  ended: null,
  responseActive: false,
  pendingResponse: null,
  toolsInFlight: 0,
  replyTimer: null,
  silenceTimer: null,
  maxDurationTimer: null,
  awaitingSilenceReply: false,
  currentResponseText: "",
  config: {},
  unanswered: false,
  lastDictation: false,
  speechStartedAt: null,
  lastSpeechDurationMs: null,
  keywords: [],
});

const normalizeForKeywordMatch = (text) =>
  String(text || "")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{Nd}]+/gu, " ")
    .trim();

const isOnlyKeywords = (transcript, keywords) => {
  const keywordWords = keywords
    .map(normalizeForKeywordMatch)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .map((keyword) => keyword.split(" "));
  let words = normalizeForKeywordMatch(transcript).split(" ").filter(Boolean);
  if (!words.length || !keywordWords.length) return false;
  while (words.length) {
    const match = keywordWords.find((keyword) => keyword.every((word, index) => words[index] === word));
    if (!match) return false;
    words = words.slice(match.length);
  }
  return true;
};

const STATUS_COLORS = {
  idle: "text.secondary",
  connecting: "warning.main",
  listening: "info.main",
  thinking: "text.secondary",
  speaking: "success.main",
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },
  title: {
    fontWeight: 600,
    fontSize: 15,
    flex: 1,
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    ...theme.scrollbarStyles,
  },
  empty: {
    margin: "auto",
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: 13,
    maxWidth: 260,
    lineHeight: 1.6,
  },
  row: {
    display: "flex",
  },
  rowEnd: {
    justifyContent: "flex-end",
  },
  bubble: {
    maxWidth: "88%",
    padding: theme.spacing(1, 1.5),
    borderRadius: 10,
    fontSize: 13.5,
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  bubbleCustomer: {
    background: theme.palette.mode === "dark" ? "rgba(66,165,245,0.16)" : "rgba(33,150,243,0.08)",
    border: `1px solid ${theme.palette.mode === "dark" ? "rgba(66,165,245,0.35)" : "rgba(33,150,243,0.22)"}`,
    borderTopRightRadius: 2,
  },
  bubbleBot: {
    background: theme.palette.action.hover,
    borderTopLeftRadius: 2,
  },
  bubbleLabel: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 2,
    opacity: 0.7,
  },
  toolSummary: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    maxWidth: "100%",
    padding: theme.spacing(0.25, 0),
    marginBottom: theme.spacing(0.75),
    border: 0,
    background: "none",
    cursor: "pointer",
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontFamily: "inherit",
    textAlign: "left",
    "&:hover": { color: theme.palette.text.primary },
  },
  toolSummaryText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  toolChevron: {
    fontSize: 16,
    transition: "transform .15s ease",
    flexShrink: 0,
  },
  toolChevronOpen: {
    transform: "rotate(90deg)",
  },
  toolDetail: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    margin: theme.spacing(0, 0, 1),
    padding: theme.spacing(0.75, 1),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
  },
  stepRow: {
    display: "grid",
    gridTemplateColumns: "14px minmax(0, 1fr) auto auto",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0.5),
    borderRadius: 6,
    border: 0,
    background: "none",
    color: "inherit",
    fontFamily: "inherit",
    textAlign: "left",
    cursor: "pointer",
    width: "100%",
    "&:hover": { background: theme.palette.action.hover },
  },
  stepNested: {
    paddingLeft: theme.spacing(2.5),
  },
  stepName: {
    fontSize: 12.5,
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  stepKind: {
    fontSize: 10.5,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  stepTime: {
    fontSize: 11.5,
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
    minWidth: 38,
    textAlign: "right",
  },
  stepOk: {
    fontSize: 13,
    color: theme.palette.success.main,
  },
  stepFail: {
    fontSize: 13,
    color: theme.palette.error.main,
  },
  stepBody: {
    margin: theme.spacing(0.25, 0, 0.75, 2.75),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  stepLabel: {
    fontSize: 10.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    color: theme.palette.text.secondary,
  },
  stepCode: {
    fontFamily: "ui-monospace, SFMono-Regular, Consolas, monospace",
    fontSize: 11.5,
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    maxHeight: 160,
    overflowY: "auto",
    padding: theme.spacing(0.5, 0.75),
    borderRadius: 6,
    background: theme.palette.action.hover,
    ...theme.scrollbarStyles,
  },
  systemNote: {
    alignSelf: "center",
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    textAlign: "center",
  },
  footer: {
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  correctButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    marginTop: theme.spacing(0.75),
    padding: 0,
    border: 0,
    background: "none",
    color: theme.palette.text.secondary,
    fontSize: 11.5,
    fontFamily: "inherit",
    cursor: "pointer",
    "&:hover": { color: theme.palette.primary.main },
  },
  coachNote: {
    alignSelf: "stretch",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    border: `1px dashed ${theme.palette.success.main}`,
    fontSize: 12.5,
    color: theme.palette.text.primary,
  },
  coachNoteText: {
    flex: 1,
    fontSize: 12.5,
  },
  thinking: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.25, 0),
  },
  thinkingSpark: {
    display: "inline-block",
    fontSize: 16,
    lineHeight: 1,
    color: theme.palette.primary.main,
    animation: "promptTestSpark 1.8s ease-in-out infinite",
    "@keyframes promptTestSpark": {
      "0%": { transform: "rotate(0deg) scale(0.85)", opacity: 0.6 },
      "50%": { transform: "rotate(180deg) scale(1.15)", opacity: 1 },
      "100%": { transform: "rotate(360deg) scale(0.85)", opacity: 0.6 },
    },
  },
  thinkingText: {
    fontSize: 13.5,
    fontWeight: 500,
    backgroundImage: `linear-gradient(90deg, ${theme.palette.text.secondary} 0%, ${theme.palette.text.primary} 50%, ${theme.palette.text.secondary} 100%)`,
    backgroundSize: "200% 100%",
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    color: "transparent",
    animation: "promptTestShimmer 1.6s linear infinite",
    "@keyframes promptTestShimmer": {
      "0%": { backgroundPosition: "100% 0" },
      "100%": { backgroundPosition: "-100% 0" },
    },
  },
  modeBar: {
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },
  footerBar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minHeight: 32,
  },
  footerHint: {
    flex: 1,
    fontSize: 11,
    color: theme.palette.text.disabled,
    textAlign: "right",
  },
  voicePanel: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minHeight: 40,
  },
  voiceStatus: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: 0.3,
  },
  statusDot: {
    width: 9,
    height: 9,
    borderRadius: "50%",
    background: "currentColor",
  },
  statusDotLive: {
    animation: "promptTestPulse 1.2s ease-in-out infinite",
    "@keyframes promptTestPulse": {
      "0%, 100%": { opacity: 1 },
      "50%": { opacity: 0.25 },
    },
  },
}));

const humanizeToolName = (name) =>
  String(name || "")
    .replace(/^(consultar_|herramienta_)/, "")
    .replace(/_\d+$/, "")
    .replace(/_/g, " ")
    .trim();

const mainToolEntries = (trace) => {
  const specialists = trace.filter((entry) => entry.kind === "specialist" || entry.kind === "transfer");
  return specialists.length ? specialists : trace;
};

const STEP_RESULT_LIMIT = 800;

const groupSteps = (trace) => {
  const steps = [];
  let pendingChildren = [];
  trace.forEach((entry) => {
    if (entry.kind === "specialist") {
      steps.push({ ...entry, children: pendingChildren });
      pendingChildren = [];
      return;
    }
    if (entry.kind === "webhook") {
      pendingChildren.push(entry);
      return;
    }
    steps.push({ ...entry, children: [] });
  });
  pendingChildren.forEach((entry) => steps.push({ ...entry, children: [] }));
  return steps;
};

const stepArgs = (args) => {
  if (!args || !Object.keys(args).length) return "";
  if (typeof args.consulta === "string" && Object.keys(args).length === 1) return args.consulta;
  return JSON.stringify(args, null, 2);
};

const truncate = (text, limit) => {
  const value = String(text ?? "");
  return value.length > limit ? `${value.slice(0, limit)}…` : value;
};

const ToolStep = ({ step, nested, classes }) => {
  const [open, setOpen] = useState(false);
  const sent = stepArgs(step.args);

  return (
    <>
      <button
        type="button"
        className={`${classes.stepRow} ${nested ? classes.stepNested : ""}`}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={step.ok === false ? classes.stepFail : classes.stepOk}>{step.ok === false ? "✕" : "✓"}</span>
        <span className={classes.stepName}>{humanizeToolName(step.name)}</span>
        <span className={classes.stepKind}>{i18n.t(`promptTestChat.kinds.${step.kind}`)}</span>
        <span className={classes.stepTime}>{((Number(step.durationMs) || 0) / 1000).toFixed(1)} s</span>
      </button>
      {open && (
        <div className={classes.stepBody}>
          {sent && (
            <>
              <span className={classes.stepLabel}>{i18n.t("promptTestChat.sent")}</span>
              <div className={classes.stepCode}>{sent}</div>
            </>
          )}
          <span className={classes.stepLabel}>{i18n.t("promptTestChat.returned")}</span>
          <div className={classes.stepCode}>
            {step.error ? `${step.error}\n\n` : ""}
            {truncate(step.result, STEP_RESULT_LIMIT) || i18n.t("promptTestChat.noResult")}
          </div>
        </div>
      )}
      {(step.children || []).map((child, index) => (
        <ToolStep key={index} step={child} nested classes={classes} />
      ))}
    </>
  );
};

const ToolSummary = ({ trace, classes }) => {
  const [open, setOpen] = useState(false);
  const main = mainToolEntries(trace);
  const names = [...new Set(main.map((entry) => humanizeToolName(entry.name)))].join(", ");
  const seconds = Math.max(...trace.map((entry) => Number(entry.durationMs) || 0)) / 1000;
  const failed = trace.some((entry) => entry.ok === false);

  return (
    <>
      <button type="button" className={classes.toolSummary} onClick={() => setOpen((value) => !value)}>
        <BuildOutlinedIcon style={{ fontSize: 13, flexShrink: 0 }} color={failed ? "error" : "inherit"} />
        <span className={classes.toolSummaryText}>
          {i18n.t("promptTestChat.usedTools", { names })}
          {seconds > 0 ? ` · ${seconds.toFixed(1)} s` : ""}
        </span>
        <ChevronRightIcon className={`${classes.toolChevron} ${open ? classes.toolChevronOpen : ""}`} />
      </button>
      {open && (
        <div className={classes.toolDetail}>
          {groupSteps(trace).map((step, index) => (
            <ToolStep key={index} step={step} classes={classes} />
          ))}
        </div>
      )}
    </>
  );
};

const conversationMessages = (messages) =>
  messages.filter((message) => message.role === "user" || message.role === "agent");

const historyFromMessages = (messages) => {
  const turns = [];
  messages.forEach((message) => {
    if (message.role === "user") turns.push({ customer: message.text, bot: "" });
    if (message.role === "agent" && turns.length) {
      const last = turns[turns.length - 1];
      last.bot = last.bot ? `${last.bot}\n${message.text}` : message.text;
    }
  });
  return turns;
};

const chatHistoryFromMessages = (messages) =>
  messages
    .filter((message) => (message.role === "user" || message.role === "agent") && message.text && !message.pending)
    .map((message) => ({ role: message.role === "user" ? "user" : "assistant", content: message.text }));

const PromptTestChat = ({ promptId, promptType, draftPrompt, savedPrompt, onApplyDraft, onSpecialistSaved }) => {
  const { classes } = useStyles();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState("text");
  const [voiceStatus, setVoiceStatus] = useState("idle");
  const [muted, setMuted] = useState(false);
  const [voiceStreams, setVoiceStreams] = useState({ local: null, remote: null });
  const [coachIndex, setCoachIndex] = useState(null);
  const [replaying, setReplaying] = useState(false);
  const bodyRef = useRef(null);
  const messagesRef = useRef([]);
  const voiceRef = useRef(emptyVoiceState());

  const type = promptType || "text";
  const ended = messages.find((message) => message.ended)?.ended || null;
  const testingDraft = Boolean(promptId) && (draftPrompt || "") !== (savedPrompt || "");
  const inCall = voiceStatus !== "idle";

  const updateMessages = useCallback((updater) => {
    setMessages((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      messagesRef.current = next;
      return next;
    });
  }, []);

  const pushMessage = useCallback((message) => updateMessages((prev) => [...prev, message]), [updateMessages]);

  const stopVoice = useCallback(() => {
    const voice = voiceRef.current;
    try {
      voice.dc?.close();
    } catch {
    }
    try {
      voice.pc?.close();
    } catch {
    }
    voice.stream?.getTracks().forEach((track) => track.stop());
    if (voice.audio) voice.audio.srcObject = null;
    if (voice.replyTimer) clearTimeout(voice.replyTimer);
    if (voice.silenceTimer) clearTimeout(voice.silenceTimer);
    if (voice.maxDurationTimer) clearTimeout(voice.maxDurationTimer);
    voiceRef.current = emptyVoiceState();
    setVoiceStreams({ local: null, remote: null });
    setVoiceStatus("idle");
    setMuted(false);
  }, []);

  const resetChat = () => {
    stopVoice();
    updateMessages([]);
    setInput("");
  };

  useEffect(() => {
    stopVoice();
    updateMessages([]);
    setInput("");
    setMode("text");
  }, [promptId, stopVoice, updateMessages]);

  useEffect(() => () => stopVoice(), [stopVoice]);

  useEffect(() => {
    const body = bodyRef.current;
    if (body) body.scrollTop = body.scrollHeight;
  }, [messages, sending]);

  const sendText = async () => {
    const text = input.trim();
    if (!text || sending || !promptId || ended) return;
    setInput("");
    await sendTextMessage(text);
  };

  const sendTextMessage = async (text, { userAlreadyShown = false } = {}) => {
    const history = historyFromMessages(conversationMessages(messagesRef.current));
    setSending(true);
    if (!userAlreadyShown) pushMessage({ role: "user", text });
    pushMessage({ role: "agent", text: "", pending: true, trace: [] });

    try {
      const { data } = await api.post(`/prompt/${promptId}/test`, {
        texto: text,
        historial: history,
        borrador: draftPrompt,
      });
      updateMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: "agent",
          text: data.botReply || i18n.t("promptTestChat.silent"),
          trace: data.trace || [],
        };
        if (data.ended) next.push({ role: "system", ended: data.ended });
        return next;
      });
    } catch (err) {
      updateMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: "agent",
          text: i18n.t("promptTestChat.error", { error: err?.response?.data?.error || err.message }),
          trace: [],
        };
        return next;
      });
    } finally {
      setSending(false);
    }
  };

  const replayConversation = async (texts) => {
    if (replaying || !texts?.length) return;
    stopVoice();
    setMode("text");
    updateMessages([]);
    setReplaying(true);
    try {
      for (const text of texts) {
        // eslint-disable-next-line no-await-in-loop
        await sendTextMessage(text);
      }
    } finally {
      setReplaying(false);
    }
  };

  const replayTextsUntil = (index) =>
    messagesRef.current
      .slice(0, index)
      .filter((message) => message.role === "user" && message.text && !message.pending)
      .map((message) => message.text);

  const handleApplyDraft = (newPrompt, versionNote) => {
    const texts = replayTextsUntil(coachIndex);
    onApplyDraft?.(newPrompt, versionNote);
    pushMessage({ role: "system", coach: true, note: i18n.t("promptTestChat.coachApplied"), replay: texts });
  };

  const handleSpecialistSaved = (name) => {
    const texts = replayTextsUntil(coachIndex);
    onSpecialistSaved?.();
    pushMessage({ role: "system", coach: true, note: i18n.t("promptTestChat.coachSavedSpecialist", { name }), replay: texts });
  };

  const sendEvent = (event) => {
    const dc = voiceRef.current.dc;
    if (dc && dc.readyState === "open") dc.send(JSON.stringify(event));
  };

  const requestResponse = (response = null) => {
    const voice = voiceRef.current;
    if (voice.responseActive) {
      voice.pendingResponse = response || {};
      return;
    }
    voice.responseActive = true;
    sendEvent(response && Object.keys(response).length ? { type: "response.create", response } : { type: "response.create" });
  };

  const finishCall = (reason, note) => {
    if (note) pushMessage({ role: "system", note });
    setTimeout(() => {
      pushMessage({ role: "system", ended: reason });
      stopVoice();
    }, END_CALL_DELAY_MS);
  };

  const clearSilenceTimer = () => {
    const voice = voiceRef.current;
    if (voice.silenceTimer) clearTimeout(voice.silenceTimer);
    voice.silenceTimer = null;
  };

  const scheduleSilenceWarning = (delayMs) => {
    const voice = voiceRef.current;
    const seconds = Number(voice.config.silenceWarningSeconds);
    if (!(seconds > 0)) return;
    clearSilenceTimer();
    voice.silenceTimer = setTimeout(() => {
      voice.silenceTimer = null;
      voice.awaitingSilenceReply = true;
      pushMessage({ role: "system", note: i18n.t("promptTestChat.silenceWarning") });
      requestResponse({
        instructions: `Diga exactamente esta frase, una sola vez, tal cual, sin agregar nada antes ni después: "${voice.config.silenceWarningPhrase}"`,
        tool_choice: "none",
      });
    }, delayMs + seconds * 1000);
  };

  const scheduleSilenceHangup = (delayMs) => {
    const voice = voiceRef.current;
    const seconds = Number(voice.config.silenceHangupSeconds);
    if (!(seconds > 0)) return;
    clearSilenceTimer();
    voice.silenceTimer = setTimeout(() => {
      voice.silenceTimer = null;
      finishCall("hangup", i18n.t("promptTestChat.silenceHangup"));
    }, delayMs + seconds * 1000);
  };

  const resetSilenceTimer = () => {
    voiceRef.current.awaitingSilenceReply = false;
    scheduleSilenceWarning(0);
  };

  const onAiFinishedSpeaking = () => {
    if (voiceRef.current.awaitingSilenceReply) scheduleSilenceHangup(0);
    else scheduleSilenceWarning(0);
  };

  const cancelReply = () => {
    const voice = voiceRef.current;
    if (voice.replyTimer) clearTimeout(voice.replyTimer);
    voice.replyTimer = null;
  };

  const scheduleReply = () => {
    cancelReply();
    const voice = voiceRef.current;
    if (!voice.unanswered) return;
    voice.replyTimer = setTimeout(() => {
      voice.replyTimer = null;
      voice.unanswered = false;
      requestResponse();
    }, voice.lastDictation ? DICTATION_DEBOUNCE_MS : REPLY_DEBOUNCE_MS);
  };

  const discardUserTurn = (itemId, note, deleteItem) => {
    if (deleteItem && itemId) sendEvent({ type: "conversation.item.delete", item_id: itemId });
    updateMessages((prev) =>
      prev.map((message) =>
        message.itemId === itemId ? { role: "system", note } : message
      )
    );
  };

  const resolveVoiceTool = async (event) => {
    const voice = voiceRef.current;
    voice.toolsInFlight += 1;
    let args = {};
    try {
      args = JSON.parse(event.arguments || "{}");
    } catch {
    }

    try {
      const { data } = await api.post(`/prompt/${promptId}/test-voice/tool`, {
        nombre: event.name,
        args,
        historial: chatHistoryFromMessages(messagesRef.current),
      });
      voice.pendingTrace = [...voice.pendingTrace, ...(data.trace || [])];
      sendEvent({
        type: "conversation.item.create",
        item: { type: "function_call_output", call_id: event.call_id, output: JSON.stringify(data.output) },
      });
      if (data.ended) {
        voice.ended = data.ended;
        const alreadyAnnounced =
          data.ended === "transferred" &&
          voice.config.transferMentionPattern &&
          new RegExp(voice.config.transferMentionPattern, "i").test(voice.currentResponseText);
        if (alreadyAnnounced) {
          voice.ended = null;
          finishCall(data.ended);
        } else {
          requestResponse(data.followUpInstructions ? { instructions: data.followUpInstructions, tool_choice: "none" } : null);
        }
      } else {
        requestResponse();
      }
    } catch (err) {
      sendEvent({
        type: "conversation.item.create",
        item: {
          type: "function_call_output",
          call_id: event.call_id,
          output: JSON.stringify({ ok: false, error: "No se pudo usar esa herramienta en este momento." }),
        },
      });
      requestResponse();
    } finally {
      voice.toolsInFlight = Math.max(0, voice.toolsInFlight - 1);
    }
  };

  const handleTranscription = (event) => {
    const voice = voiceRef.current;
    const transcript = String(event.transcript || "").trim();
    const durationMs = voice.lastSpeechDurationMs;
    voice.lastSpeechDurationMs = null;

    if (!transcript) {
      discardUserTurn(event.item_id, i18n.t("promptTestChat.discardedSilence"), false);
      scheduleReply();
      return;
    }
    if (durationMs !== null && durationMs < MIN_SPEECH_DURATION_MS) {
      discardUserTurn(event.item_id, i18n.t("promptTestChat.discardedNoise", { text: transcript }), false);
      scheduleReply();
      return;
    }
    if (!REAL_SPEECH_PATTERN.test(transcript)) {
      discardUserTurn(event.item_id, i18n.t("promptTestChat.discardedNoise", { text: transcript }), false);
      scheduleReply();
      return;
    }
    if (isOnlyKeywords(transcript, voice.keywords)) {
      discardUserTurn(event.item_id, i18n.t("promptTestChat.discardedKeywords", { text: transcript }), true);
      scheduleReply();
      return;
    }
    if (voice.toolsInFlight > 0) {
      discardUserTurn(event.item_id, i18n.t("promptTestChat.discardedDuringTool", { text: transcript }), true);
      return;
    }

    updateMessages((prev) =>
      prev.map((message) => (message.itemId === event.item_id ? { ...message, text: transcript, pending: false } : message))
    );
    sendEvent({
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "system",
        content: [{ type: "input_text", text: transcriptNote(transcript) }],
      },
    });
    voice.unanswered = true;
    voice.lastDictation = DICTATION_PATTERN.test(transcript);
    resetSilenceTimer();
    scheduleReply();
  };

  const handleVoiceEvent = (event) => {
    const voice = voiceRef.current;
    switch (event.type) {
      case "input_audio_buffer.speech_started":
        voice.speechStartedAt = Date.now();
        cancelReply();
        clearSilenceTimer();
        setVoiceStatus("listening");
        break;
      case "input_audio_buffer.speech_stopped":
        voice.lastSpeechDurationMs = voice.speechStartedAt ? Date.now() - voice.speechStartedAt : null;
        voice.speechStartedAt = null;
        onAiFinishedSpeaking();
        break;
      case "input_audio_buffer.committed":
        pushMessage({ role: "user", text: "…", pending: true, itemId: event.item_id });
        break;
      case "conversation.item.input_audio_transcription.completed":
        handleTranscription(event);
        break;
      case "conversation.item.input_audio_transcription.failed":
        scheduleReply();
        break;
      case "response.created":
        voice.responseActive = true;
        voice.currentResponseText = "";
        clearSilenceTimer();
        setVoiceStatus("thinking");
        break;
      case "response.output_audio_transcript.delta": {
        setVoiceStatus("speaking");
        const delta = String(event.delta || "");
        if (!delta) break;
        voice.currentResponseText += delta;
        const exists = messagesRef.current.some((message) => message.streamId === event.item_id);
        if (exists) {
          updateMessages((prev) =>
            prev.map((message) =>
              message.streamId === event.item_id ? { ...message, text: message.text + delta } : message
            )
          );
        } else {
          pushMessage({ role: "agent", text: delta, streamId: event.item_id, trace: voice.pendingTrace });
          voice.pendingTrace = [];
        }
        break;
      }
      case "response.output_audio_transcript.done": {
        const text = String(event.transcript || "").trim();
        if (!text) break;
        const exists = messagesRef.current.some((message) => message.streamId === event.item_id);
        if (exists) {
          updateMessages((prev) =>
            prev.map((message) => (message.streamId === event.item_id ? { ...message, text } : message))
          );
        } else {
          pushMessage({ role: "agent", text, streamId: event.item_id, trace: voice.pendingTrace });
          voice.pendingTrace = [];
        }
        break;
      }
      case "response.function_call_arguments.done":
        resolveVoiceTool(event);
        break;
      case "output_audio_buffer.stopped":
        setVoiceStatus("listening");
        if (!voice.responseActive && voice.toolsInFlight === 0 && !voice.ended) onAiFinishedSpeaking();
        break;
      case "response.done": {
        voice.responseActive = false;
        if (voice.pendingResponse) {
          const next = voice.pendingResponse;
          voice.pendingResponse = null;
          requestResponse(next);
        }
        if (voice.ended && !voice.responseActive && voice.toolsInFlight === 0) {
          const reason = voice.ended;
          voice.ended = null;
          setTimeout(() => {
            pushMessage({ role: "system", ended: reason });
            stopVoice();
          }, END_CALL_DELAY_MS);
        }
        break;
      }
      case "error":
        if (String(event.error?.code || "") === "conversation_already_has_active_response") {
          voice.responseActive = true;
          break;
        }
        pushMessage({ role: "system", note: event.error?.message || i18n.t("promptTestChat.voiceError") });
        break;
      default:
        break;
    }
  };

  const startVoice = async () => {
    if (!promptId || inCall) return;
    setVoiceStatus("connecting");

    try {
      const { data } = await api.post(`/prompt/${promptId}/test-voice/session`, {
        borrador: draftPrompt,
      });

      if (data.connectionName) {
        pushMessage({ role: "system", note: i18n.t("promptTestChat.connectionUsed", { name: data.connectionName }) });
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const pc = new RTCPeerConnection();
      const audio = new Audio();
      audio.autoplay = true;
      pc.ontrack = (event) => {
        audio.srcObject = event.streams[0];
        setVoiceStreams((prev) => ({ ...prev, remote: event.streams[0] }));
        audio.play().catch(() => {});
      };
      stream.getAudioTracks().forEach((track) => pc.addTrack(track, stream));
      setVoiceStreams({ local: stream, remote: null });

      const dc = pc.createDataChannel("oai-events");
      voiceRef.current = {
        ...emptyVoiceState(),
        pc,
        dc,
        stream,
        audio,
        keywords: data.phantomKeywords || [],
        config: {
          silenceWarningSeconds: data.silenceWarningSeconds,
          silenceHangupSeconds: data.silenceHangupSeconds,
          silenceWarningPhrase: data.silenceWarningPhrase,
          transferMentionPattern: data.transferMentionPattern,
        },
      };

      dc.onopen = () => {
        setVoiceStatus("listening");
        if (data.greetingInstructions) {
          requestResponse({ instructions: data.greetingInstructions, tool_choice: "none" });
        }
        resetSilenceTimer();
        if (Number(data.maxDurationMinutes) > 0) {
          voiceRef.current.maxDurationTimer = setTimeout(
            () => finishCall("hangup", i18n.t("promptTestChat.maxDurationReached")),
            Number(data.maxDurationMinutes) * 60 * 1000
          );
        }
      };
      dc.onmessage = (message) => {
        try {
          handleVoiceEvent(JSON.parse(message.data));
        } catch {
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const response = await fetch(OPENAI_CALLS_URL, {
        method: "POST",
        headers: { Authorization: `Bearer ${data.ephemeralToken}`, "Content-Type": "application/sdp" },
        body: pc.localDescription.sdp,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      await pc.setRemoteDescription({ type: "answer", sdp: await response.text() });
    } catch (err) {
      stopVoice();
      pushMessage({
        role: "system",
        note: i18n.t("promptTestChat.voiceStartError", { error: err?.response?.data?.error || err.message }),
      });
    }
  };

  const toggleMute = () => {
    const next = !muted;
    voiceRef.current.stream?.getAudioTracks().forEach((track) => {
      track.enabled = !next;
    });
    setMuted(next);
  };

  const changeMode = (_, value) => {
    if (!value || value === mode) return;
    stopVoice();
    setMode(value);
  };

  const deliverVoiceText = (text, { userAlreadyShown = false } = {}) => {
    const dc = voiceRef.current.dc;
    if (!dc || dc.readyState !== "open") return;
    if (!userAlreadyShown) pushMessage({ role: "user", text });
    sendEvent({
      type: "conversation.item.create",
      item: { type: "message", role: "user", content: [{ type: "input_text", text }] },
    });
    cancelReply();
    voiceRef.current.unanswered = false;
    requestResponse();
  };

  const sendVoiceText = async () => {
    const text = input.trim();
    const dc = voiceRef.current.dc;
    if (!text || !dc || dc.readyState !== "open" || sending) return;
    setInput("");
    deliverVoiceText(text);
  };

  const submitInput = () => (mode === "voice" ? sendVoiceText() : sendText());

  const onKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submitInput();
    }
  };

  const voiceInputReady = mode === "voice" && inCall && voiceStatus !== "connecting";
  const inputDisabled = mode === "voice" ? !voiceInputReady : !promptId || Boolean(ended);

  const renderInput = () => (
    <TextField
      fullWidth
      multiline
      maxRows={4}
      size="small"
      placeholder={
        mode === "voice"
          ? i18n.t("promptTestChat.voiceTypePlaceholder")
          : ended
            ? i18n.t("promptTestChat.endedPlaceholder")
            : i18n.t("promptTestChat.placeholder")
      }
      value={input}
      onChange={(e) => setInput(e.target.value)}
      onKeyDown={onKeyDown}
      disabled={inputDisabled}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={submitInput} disabled={!input.trim() || sending || inputDisabled}>
                {sending ? <CircularProgress size={16} /> : <SendOutlinedIcon fontSize="small" />}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );

  return (
    <Paper variant="outlined" className={classes.root}>
      <CoachDialog
        open={coachIndex !== null}
        onClose={() => setCoachIndex(null)}
        promptId={promptId}
        draftPrompt={draftPrompt}
        messages={messages}
        correctedIndex={coachIndex ?? 0}
        onApplyToDraft={handleApplyDraft}
        onSavedSpecialist={handleSpecialistSaved}
      />
      <div className={classes.header}>
        <Typography className={classes.title}>{i18n.t("promptTestChat.title")}</Typography>
        {testingDraft && (
          <Tooltip title={i18n.t("promptTestChat.draftHelp")}>
            <Chip size="small" color="warning" variant="outlined" label={i18n.t("promptTestChat.draft")} />
          </Tooltip>
        )}
        <Tooltip title={i18n.t("promptTestChat.newChat")}>
          <span>
            <IconButton size="small" onClick={resetChat} disabled={sending || (!messages.length && !inCall)}>
              <AddCommentOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </div>


      <div className={classes.modeBar}>
        <ToggleButtonGroup size="small" exclusive fullWidth value={mode} onChange={changeMode}>
          <ToggleButton value="text">
            <ChatBubbleOutlineOutlinedIcon fontSize="small" style={{ marginRight: 6 }} />
            {i18n.t("promptTestChat.modeText")}
          </ToggleButton>
          <ToggleButton value="voice">
            <GraphicEqOutlinedIcon fontSize="small" style={{ marginRight: 6 }} />
            {i18n.t("promptTestChat.modeVoice")}
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      <div className={classes.body} ref={bodyRef}>
        {!promptId && <Typography className={classes.empty}>{i18n.t("promptTestChat.saveFirst")}</Typography>}
        {promptId && !messages.length && (
          <Typography className={classes.empty}>
            {i18n.t(mode === "voice" ? "promptTestChat.emptyVoice" : "promptTestChat.empty")}
          </Typography>
        )}

        {messages.map((message, index) => {
          if (message.role === "system" && message.coach) {
            return (
              <div key={index} className={classes.coachNote}>
                <EditNoteOutlinedIcon fontSize="small" color="success" />
                <Typography className={classes.coachNoteText}>{message.note}</Typography>
                {message.replay?.length > 0 && (
                  <Button
                    size="small"
                    startIcon={<ReplayOutlinedIcon fontSize="small" />}
                    onClick={() => replayConversation(message.replay)}
                    disabled={replaying || sending}
                  >
                    {i18n.t("promptTestChat.replay")}
                  </Button>
                )}
              </div>
            );
          }

          if (message.role === "system") {
            return (
              <Typography key={index} className={classes.systemNote}>
                {message.ended
                  ? i18n.t(message.ended === "transferred" ? "promptTestChat.transferred" : "promptTestChat.hungUp")
                  : message.note}
              </Typography>
            );
          }

          if (message.role === "user") {
            return (
              <div key={index} className={`${classes.row} ${classes.rowEnd}`}>
                <div className={`${classes.bubble} ${classes.bubbleCustomer}`}>
                  <div className={classes.bubbleLabel}>
                    <PersonOutlineOutlinedIcon style={{ fontSize: 12 }} />
                    {i18n.t("promptTestChat.you")}
                  </div>
                  {message.text}
                </div>
              </div>
            );
          }

          return (
            <div key={index} className={classes.row}>
              <div className={`${classes.bubble} ${classes.bubbleBot}`}>
                <div className={classes.bubbleLabel}>
                  <SmartToyOutlinedIcon style={{ fontSize: 12 }} />
                  {i18n.t("promptTestChat.agent")}
                </div>
                {message.trace?.length > 0 && <ToolSummary trace={message.trace} classes={classes} />}
                {message.pending ? (
                  <span className={classes.thinking}>
                    <span className={classes.thinkingSpark}>✻</span>
                    <span className={classes.thinkingText}>{i18n.t("promptTestChat.thinking")}</span>
                  </span>
                ) : (
                  <div>{message.text}</div>
                )}
                {!message.pending && message.text && promptId && (
                  <button type="button" className={classes.correctButton} onClick={() => setCoachIndex(index)}>
                    <EditNoteOutlinedIcon style={{ fontSize: 14 }} />
                    {i18n.t("promptTestChat.correct")}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className={classes.footer}>
        {mode === "text" ? (
          renderInput()
        ) : (
          <>
          {inCall && renderInput()}
          <VoiceBars localStream={voiceStreams.local} remoteStream={voiceStreams.remote} active={inCall} />
          <div className={classes.voicePanel}>
            <Typography className={classes.voiceStatus} sx={{ color: STATUS_COLORS[voiceStatus] }}>
              <span className={`${classes.statusDot} ${inCall ? classes.statusDotLive : ""}`} />
              {i18n.t(`promptTestChat.status.${voiceStatus}`)}
            </Typography>
            {inCall && (
              <Tooltip title={i18n.t(muted ? "promptTestChat.unmute" : "promptTestChat.mute")}>
                <span>
                  <IconButton
                    size="small"
                    color={muted ? "error" : "default"}
                    onClick={toggleMute}
                    disabled={voiceStatus === "connecting"}
                    sx={{ border: 1, borderColor: "divider" }}
                  >
                    {muted ? <MicOffOutlinedIcon fontSize="small" /> : <MicOutlinedIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {inCall ? (
              <Button
                size="small"
                variant="contained"
                color="error"
                startIcon={<CallEndOutlinedIcon fontSize="small" />}
                onClick={stopVoice}
              >
                {i18n.t("promptTestChat.endCall")}
              </Button>
            ) : (
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CallOutlinedIcon fontSize="small" />}
                onClick={startVoice}
                disabled={!promptId}
              >
                {i18n.t("promptTestChat.startCall")}
              </Button>
            )}
          </div>
          </>
        )}

        {(mode === "voice" || ended) && (
          <div className={classes.footerBar}>
            <Typography className={classes.footerHint}>
              {mode === "voice" && i18n.t("promptTestChat.voiceHint")}
              {mode === "text" && ended && (
                <Button size="small" onClick={resetChat}>
                  {i18n.t("promptTestChat.newChat")}
                </Button>
              )}
            </Typography>
          </div>
        )}
      </div>
    </Paper>
  );
};

export default PromptTestChat;
